/**
 * Builds document sharing payload according to hierarchical sharing rules:
 * 1. If users are selected → share only with users (ignore branch/dept/sub-dept)
 * 2. If users are not selected but sub-departments are → share with sub-departments only (ignore branch/dept)
 * 3. If users and sub-departments are not selected but departments are → share with departments only (ignore branch)
 * 4. If only branch is selected → share with branch only
 * 
 * @param {Array} branchIds - Selected branch IDs
 * @param {Array} departmentIds - Selected department IDs
 * @param {Array} subDepartmentIds - Selected sub-department IDs
 * @param {Array} userIds - Selected user IDs
 * @returns {Object} Payload object with only the relevant sharing fields
 */
export const buildDocumentSharePayload = (branchIds = [], departmentIds = [], subDepartmentIds = [], userIds = []) => {
  const payload = {
    branch_ids: [],
    department_ids: [],
    sub_department_ids: [],
    user_ids: []
  };

  // Hierarchy: Users > Sub-Departments > Departments > Branches
  if (userIds && userIds.length > 0) {
    // If users are selected, share only with users
    payload.user_ids = userIds;
  } else if (subDepartmentIds && subDepartmentIds.length > 0) {
    // If no users but sub-departments are selected, share only with sub-departments
    payload.sub_department_ids = subDepartmentIds;
  } else if (departmentIds && departmentIds.length > 0) {
    // If no users or sub-departments but departments are selected, share only with departments
    payload.department_ids = departmentIds;
  } else if (branchIds && branchIds.length > 0) {
    // If only branches are selected, share only with branches
    payload.branch_ids = branchIds;
  }

  return payload;
};

/**
 * Builds document sharing payload for FormData (used in file uploads)
 * Applies the same hierarchical sharing rules but appends to FormData instead
 * 
 * @param {FormData} formData - FormData object to append to
 * @param {Array} branchIds - Selected branch IDs
 * @param {Array} departmentIds - Selected department IDs
 * @param {Array} subDepartmentIds - Selected sub-department IDs
 * @param {Array} userIds - Selected user IDs
 */
export const appendDocumentShareToFormData = (formData, branchIds = [], departmentIds = [], subDepartmentIds = [], userIds = []) => {
  // Hierarchy: Users > Sub-Departments > Departments > Branches
  if (userIds && userIds.length > 0) {
    // If users are selected, share only with users
    formData.append('user_ids', JSON.stringify(userIds));
  } else if (subDepartmentIds && subDepartmentIds.length > 0) {
    // If no users but sub-departments are selected, share only with sub-departments
    formData.append('sub_department_ids', JSON.stringify(subDepartmentIds));
  } else if (departmentIds && departmentIds.length > 0) {
    // If no users or sub-departments but departments are selected, share only with departments
    formData.append('department_ids', JSON.stringify(departmentIds));
  } else if (branchIds && branchIds.length > 0) {
    // If only branches are selected, share only with branches
    formData.append('branch_ids', JSON.stringify(branchIds));
  }
};

